import type { Dispatch } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { REJECTION_MESSAGES, REJECTION_REASON } from '../../constants/rejectionMessages';
import { KYC_STATUS } from '../../types/kyc';
import type { KYCFlowState, KYCAction } from '../KYCFlowController';

interface RejectionScreenProps {
  state: KYCFlowState;
  dispatch: Dispatch<KYCAction>;
  // Llamado cuando el usuario cierra la pantalla de rechazo definitivo
  onDismiss?: () => void;
}

const MAX_RETRIES = 3;

// Pantalla de rechazo: muestra razón, instrucciones y botón de reintento si aplica
export function RejectionScreen({ state, dispatch, onDismiss }: RejectionScreenProps) {
  const reason = state.rejectionReason;
  const message = reason ? REJECTION_MESSAGES[reason] : null;

  // Determina el origen del rechazo para saber qué acción de reintento despachar.
  // wasValidatingPOA se basa en poaRetries porque idScore puede estar seteado desde
  // la clasificación incluso cuando el rechazo ocurrió durante AWAITING_EXTRACTION_SCORE
  // (service_error, data_mismatch, doc_low_score de extracción), lo que haría que la
  // condición anterior (idScore !== null && poaScore === null) diera falso positivo.
  const isLivenessRejection =
    reason === REJECTION_REASON.LIVENESS_FAILED ||
    reason === REJECTION_REASON.FACE_COMPARISON_FAILED;
  const wasValidatingPOA = state.poaRetries > 0;

  const canRetryAction = message?.canRetry && reason !== REJECTION_REASON.MAX_RETRIES;
  const retriesExhausted = isLivenessRejection
    ? state.livenessRetries >= MAX_RETRIES
    : (wasValidatingPOA && state.poaRetries >= MAX_RETRIES) ||
      (!wasValidatingPOA && state.idRetries >= MAX_RETRIES);

  const handleRetry = () => {
    if (isLivenessRejection) {
      dispatch({ type: 'RETRY_LIVENESS' });
    } else if (wasValidatingPOA) {
      dispatch({ type: 'RETRY_POA' });
    } else {
      dispatch({ type: 'RETRY_ID' });
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        gap: 2,
        textAlign: 'center',
      }}
    >
      <CancelIcon sx={{ fontSize: 72, color: 'error.main' }} />

      <Typography variant="h4" fontWeight={700}>
        {message?.title ?? 'Verificación rechazada'}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
        {message?.description ?? 'No fue posible completar la verificación de identidad.'}
      </Typography>

      {/* Botón de reintento si la razón lo permite y no se agotaron los intentos */}
      {canRetryAction && !retriesExhausted && (
        <Button variant="contained" color="primary" onClick={handleRetry} sx={{ mt: 2 }}>
          Intentar de nuevo
        </Button>
      )}

      {/* Instrucción de soporte cuando no hay más intentos */}
      {(!canRetryAction || retriesExhausted) && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          ¿Necesitas ayuda?{' '}
          <Box
            component="a"
            href="mailto:soporte@playcity.com.mx"
            sx={{ color: 'primary.main', textDecoration: 'underline' }}
          >
            soporte@playcity.com.mx
          </Box>
        </Typography>
      )}

      {/* Botón de cierre en rechazo definitivo (sin más reintentos) */}
      {(!canRetryAction || retriesExhausted) && onDismiss && (
        <Button variant="outlined" color="error" onClick={onDismiss} sx={{ mt: 2 }}>
          Cerrar
        </Button>
      )}
    </Box>
  );
}
